import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TrustSignals = () => {
  const securityBadges = [
    {
      name: "SSL Secured",
      icon: "Shield",
      description: "256-bit encryption"
    },
    {
      name: "SOC 2 Compliant",
      icon: "CheckCircle",
      description: "Security audited"
    },
    {
      name: "GDPR Compliant",
      icon: "Lock",
      description: "Privacy protected"
    },
    {
      name: "Financial Grade",
      icon: "CreditCard",
      description: "Bank-level security"
    }
  ];

  const mediaLogos = [
    {
      name: "TechCrunch",
      logo: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=120&h=60&fit=crop"
    },
    {
      name: "Forbes",
      logo: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=120&h=60&fit=crop"
    },
    {
      name: "Wall Street Journal",
      logo: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=120&h=60&fit=crop"
    },
    {
      name: "Bloomberg",
      logo: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=120&h=60&fit=crop"
    }
  ];

  const expertTestimonials = [
    {
      name: "Dr. Amanda Foster",
      title: "Art Authentication Expert",
      company: "Sotheby\'s Institute",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
      quote: "Crazy Rare Club has revolutionized how serious collectors approach portfolio management. The authentication tools and market analysis are unparalleled.",
      rating: 5
    },
    {
      name: "Marcus Webb",
      title: "Vintage Watch Specialist",
      company: "Christie\'s Auctions",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      quote: "The real-time valuation system has helped my clients make informed decisions worth millions. It\'s become an essential tool in our industry.",
      rating: 5
    },
    {
      name: "Jennifer Liu",
      title: "Sports Memorabilia Appraiser",
      company: "Heritage Auctions",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      quote: "The platform\'s market intelligence has consistently outperformed traditional appraisal methods. My clients see 20% better returns on average.",
      rating: 5
    }
  ];

  const communityStats = [
    {
      number: "15,247",
      label: "Active Collectors",
      icon: "Users",
      growth: "+23% this month"
    },
    {
      number: "$2.4M",
      label: "Total Portfolio Value",
      icon: "DollarSign",
      growth: "+18% this quarter"
    },
    {
      number: "98.7%",
      label: "Accuracy Rate",
      icon: "Target",
      growth: "Industry leading"
    },
    {
      number: "24/7",
      label: "Market Monitoring",
      icon: "Clock",
      growth: "Real-time updates"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-muted/10 to-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Trusted by Collectors Worldwide
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Join thousands of collectors who trust our platform with their most valuable possessions and investment decisions.
          </p>
        </motion.div>

        {/* Community Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
        >
          {communityStats?.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name={stat?.icon} size={24} className="text-primary" />
              </div>
              <div className="text-3xl lg:text-4xl font-bold text-foreground mb-2">
                {stat?.number}
              </div>
              <div className="text-lg font-medium text-card-foreground mb-1">
                {stat?.label}
              </div>
              <div className="text-sm text-success">
                {stat?.growth}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Expert Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-foreground text-center mb-12">
            What Industry Experts Say
          </h3>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {expertTestimonials?.map((testimonial, index) => (
              <div key={index} className="bg-card border border-border rounded-2xl p-8 premium-hover">
                <div className="flex items-center mb-6">
                  <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="text-lg font-semibold text-card-foreground">
                      {testimonial?.name}
                    </h4>
                    <p className="text-sm text-primary font-medium">
                      {testimonial?.title}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {testimonial?.company}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center mb-4">
                  {[...Array(testimonial?.rating)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-muted-foreground leading-relaxed">
                  "{testimonial?.quote}"
                </blockquote>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Security Badges */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Enterprise-Grade Security
          </h3>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {securityBadges?.map((badge, index) => (
              <div key={index} className="text-center p-6 bg-card border border-border rounded-xl premium-hover">
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={badge?.icon} size={20} className="text-success" />
                </div>
                <h4 className="font-semibold text-card-foreground mb-2">
                  {badge?.name}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {badge?.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Media Coverage */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-xl font-semibold text-muted-foreground mb-8">
            Featured In
          </h3>
          
          <div className="flex items-center justify-center space-x-12 opacity-60">
            {mediaLogos?.map((media, index) => (
              <div key={index} className="grayscale hover:grayscale-0 transition-all duration-300">
                <Image
                  src={media?.logo}
                  alt={media?.name}
                  className="h-8 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustSignals;